import { InputHTMLAttributes, useId } from 'react'

import { cn } from '@/lib/utils'
import { ControllerRenderProps, FieldValues } from 'react-hook-form'
import {
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Spinner } from '../ui/spinner'

const InputBox = ({
  type = 'text',
  error,
  label,
  id,
  helperText,
  loading,
  labelClassName,
  maxLength,
  max,
  step,
  onChange,
  ...props
}: Props) => {
  const fallbackId = useId()
  const inputId = id ?? fallbackId
  const hasError = error ? error !== '' : false

  const handleChange = onChange
    ? (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value

        if (
          type !== 'number' &&
          maxLength !== undefined &&
          val.length > maxLength
        ) {
          e.target.value = val.slice(0, maxLength)
          onChange(e)
          return
        }

        if (type === 'number' && val !== '' && val !== '-') {
          const [intPart, decPart] = val.split('.')

          if (max !== undefined) {
            const maxNum =
              typeof max === 'string' ? parseFloat(max) : Number(max)
            if (!isNaN(maxNum)) {
              const maxIntDigits = Math.floor(Math.abs(maxNum)).toString()
                .length
              if (intPart.replace('-', '').length > maxIntDigits) return
            }
          }

          if (step !== undefined && decPart !== undefined) {
            const stepStr = step.toString()
            const maxDecimals = stepStr.includes('.')
              ? stepStr.split('.')[1].length
              : 0
            if (decPart.length > maxDecimals) return
          }
        }

        onChange(e)
      }
    : undefined

  return (
    <FormItem>
      {label && (
        <FormLabel
          htmlFor={inputId}
          className={cn('gap-1', error && 'text-destructive', labelClassName)}
        >
          {label}
          {props.required && <span className="text-destructive">*</span>}
        </FormLabel>
      )}
      <FormControl>
        <div className="relative">
          <Input
            id={inputId}
            type={type}
            maxLength={maxLength}
            max={max}
            step={step}
            onChange={handleChange}
            {...props}
          />
          {loading && (
            <Spinner className="stroke-primary absolute top-1/2 right-2 -translate-y-1/2" />
          )}
        </div>
      </FormControl>
      <FormMessage />
      {hasError && <p className="text-destructive">{error}</p>}
      {helperText && (
        <p className="text-muted-foreground text-sm">{helperText}</p>
      )}
    </FormItem>
  )
}

interface Props
  extends InputHTMLAttributes<
    HTMLInputElement | ControllerRenderProps<FieldValues, string>
  > {
  label?: string
  error?: string
  helperText?: string
  loading?: boolean
  labelClassName?: string
}

export default InputBox
